import React from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";

import { LogStatsDto } from "../../interfaces";

type LogStatsWidgetsProps = {
  stats?: LogStatsDto;
  isLoading?: boolean;
  activeLevel?: string;
  httpErrorsOnly?: boolean;
  onSelectLevel: (level: string) => void;
  onSelectHttpErrors: () => void;
  onSelectTotal: () => void;
};

type WidgetItem = {
  key: string;
  label: string;
  value: number;
  icon: string;
  accent: string;
  onClick?: () => void;
  active?: boolean;
};

export const LogStatsWidgets: React.FC<LogStatsWidgetsProps> = ({
  stats,
  isLoading,
  activeLevel,
  httpErrorsOnly,
  onSelectLevel,
  onSelectHttpErrors,
  onSelectTotal,
}) => {
  const { t } = useTranslation();

  const summaryWidgets: WidgetItem[] = [
    {
      key: "total",
      label: t("total"),
      value: stats?.total ?? 0,
      icon: "ri-file-list-3-line",
      accent: "primary",
      onClick: onSelectTotal,
      active: !activeLevel && !httpErrorsOnly,
    },
    {
      key: "errors",
      label: t("error"),
      value: stats?.errors ?? 0,
      icon: "ri-error-warning-line",
      accent: "danger",
      onClick: () => onSelectLevel("error"),
      active: activeLevel === "error",
    },
    {
      key: "warnings",
      label: t("warn"),
      value: stats?.warnings ?? 0,
      icon: "ri-alert-line",
      accent: "warning",
      onClick: () => onSelectLevel("warn"),
      active: activeLevel === "warn",
    },
    {
      key: "info",
      label: t("info"),
      value: stats?.info ?? 0,
      icon: "ri-information-line",
      accent: "success",
      onClick: () => onSelectLevel("info"),
      active: activeLevel === "info",
    },
    {
      key: "httpErrors",
      label: t("httpErrors"),
      value: stats?.httpErrors ?? 0,
      icon: "ri-shield-cross-line",
      accent: "dark",
      onClick: onSelectHttpErrors,
      active: httpErrorsOnly,
    },
    {
      key: "services",
      label: t("services"),
      value: stats?.uniqueServices ?? 0,
      icon: "ri-server-line",
      accent: "info",
    },
  ];

  return (
    <div className="logs-stats-widgets mb-3">
      <Row className="g-3">
        {summaryWidgets.map((item) => (
          <Col sm={6} md={4} xl={2} key={item.key}>
            <Card
              className={`card-animate logs-stat-card logs-stat-card--${item.accent} ${
                item.onClick ? "logs-stat-card--clickable" : ""
              } ${item.active ? "logs-stat-card--active" : ""}`}
              onClick={item.onClick}
            >
              <CardBody>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="min-w-0">
                    <p className="text-muted mb-2 text-truncate">{item.label}</p>
                    <h4 className="fs-22 fw-semibold mb-0">
                      {isLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        <CountUp start={0} end={item.value} duration={1.2} />
                      )}
                    </h4>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span
                      className={`avatar-title rounded fs-3 bg-${item.accent}-subtle text-${item.accent}`}
                    >
                      <i className={item.icon} />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};
